import { navigationRef } from "expo-router/build/global-state/navigationRef";
import { getRouteInfoFromState } from "expo-router/build/global-state/getRouteInfoFromState";
import { usePreviewInfo } from "expo-router/build/link/preview/PreviewRouteContext";
import memoize from "@-/com.npmjs.memoize";
import { useCallback, useMemo, useSyncExternalStore } from "react";

/**
 * Navigation states are immutable, and `getRootState()` returns the same object until the state
 * changes. Every scoped consumer (about ten on Learn alone) reads the snapshot on each change, so
 * resolving the full route info once per state keeps a route update from repeating that work per
 * subscriber.
 */
const getRootStatePathname = memoize(
  (state: Parameters<typeof getRouteInfoFromState>[0]) => getRouteInfoFromState(state).pathname,
  { cache: new WeakMap() },
);

function subscribeToRouteState(listener: () => void) {
  return navigationRef.addListener("state", listener);
}

// This is an imperative external-store selector, rather than React render state.
// Construct it outside the hook so React Compiler does not treat its mutable
// closure as a render-local variable that is reassigned after rendering.
function createScopedPathnameSelector(rootPath: `/${string}`) {
  let lastInScopePathname: string = rootPath;
  return (pathname: string) => {
    if (pathname === rootPath || pathname.startsWith(`${rootPath}/`)) {
      lastInScopePathname = pathname;
    }
    return lastInScopePathname;
  };
}

/**
 * `usePathname()` for chrome that belongs to ONE section of the app (a bottom tab's toolbar, a tab
 * layout): the live pathname while it is inside `rootPath`, and the last pathname that was
 * otherwise.
 *
 * Every bottom tab stays mounted, and `usePathname()` is global, so a tab's toolbar used to see
 * the OTHER tab's pathname the moment the learner switched away. Its derived state flipped (the
 * Stats pills fell back to "Overall", the Deck layout to its default route key) and the hidden
 * tab re-rendered its toolbar and content on every switch — then flipped back on return. Holding
 * the last in-scope value makes a switch invisible to the tabs that are not involved in it.
 */
export function useScopedPathname(rootPath: `/${string}`): string {
  const preview = usePreviewInfo();
  const selectPathname = useMemo(() => {
    // Select before React schedules work. Retaining the result after usePathname() has
    // already subscribed is too late: every sibling pathname still renders the consumer.
    // This cache belongs to one hook/scope, never to the shared router or another root.
    return createScopedPathnameSelector(rootPath);
  }, [rootPath]);
  const getSnapshot = useCallback(
    () =>
      selectPathname(
        preview.isPreview
          ? (preview.pathname ?? "")
          : navigationRef.isReady()
            ? getRootStatePathname(navigationRef.getRootState())
            : rootPath,
      ),
    [selectPathname, preview.isPreview, preview.pathname, rootPath],
  );
  // The imperative navigation ref is not initialized during SSR. Reading it
  // again while hydrating can instead return the browser's deep link, changing
  // the rendered tree before hydration finishes. Both renders start at the
  // scope root; the live snapshot publishes the route immediately afterwards.
  const getServerSnapshot = useCallback(
    () => (preview.isPreview ? selectPathname(preview.pathname ?? "") : rootPath),
    [preview.isPreview, preview.pathname, rootPath, selectPathname],
  );
  return useSyncExternalStore(subscribeToRouteState, getSnapshot, getServerSnapshot);
}
